import { CreditCard, Pencil } from 'lucide-react'

import { usePaymentGatewaysTable } from '@/hooks/features/payment-gateways/usePaymentGatewaysTable'
import { useDataTable } from '@/hooks/ui'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table'

import { PageHeader, StatusBadge } from '@/components/atoms'
import { PaymentGatewayModel } from '@/types/model/PaymentGateway'

import { PaymentGatewayDialog } from './PaymentGatewayDialog'

type TableColumns = 'gateway' | 'type' | 'mode' | 'status' | 'actions'

const visibleColumns: TableColumns[] = ['gateway', 'type', 'mode', 'status', 'actions']

export const PaymentGatewaysTable = () => {
    const {
        loading,
        data,
        gatewayDialogOpen,
        editGateway,
        updateLoading,
        openEditDialog,
        closeDialog,
        handleUpdateGateway,
    } = usePaymentGatewaysTable()

    const { columns } = useDataTable<PaymentGatewayModel.Gateway, TableColumns>({
        columns: [
            {
                accessorKey: 'gateway',
                columnLabel: () => 'Gateway',
                cell: (gateway) => (
                    <div className="flex items-center gap-3">
                        <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border-subtle bg-subdued">
                            {gateway.logo ? (
                                <img src={gateway.logo} alt={gateway.name} className="object-contain" />
                            ) : (
                                <CreditCard className="size-5 text-muted-foreground" />
                            )}
                        </div>
                        <div>
                            <p className="font-medium text-ink">{gateway.name}</p>
                            <p className="text-xs text-text-2">{gateway.key}</p>
                        </div>
                    </div>
                ),
            },
            {
                accessorKey: 'type',
                columnLabel: () => 'Type',
                cell: (gateway) => <span className="capitalize">{gateway.type}</span>,
            },
            {
                accessorKey: 'mode',
                columnLabel: () => 'Mode',
                cell: (gateway) => (
                    <Badge tone={gateway.credential_type === 'sandbox' ? 'warning' : 'soft'} className="capitalize">
                        {gateway.credential_type}
                    </Badge>
                ),
            },
            {
                accessorKey: 'status',
                columnLabel: () => 'Status',
                cell: (gateway) => <StatusBadge type="common" status={gateway.status} />,
            },
            {
                accessorKey: 'actions',
                columnLabel: () => 'Actions',
                cell: (gateway) => (
                    <Button variant="ghost" size="icon" aria-label="Edit" onClick={() => openEditDialog(gateway)}>
                        <Pencil className="text-muted-foreground" />
                    </Button>
                ),
                classes: () => ({
                    column: 'text-right',
                    cell: 'text-right',
                }),
                isAction: true,
            },
        ],
        visibleColumns,
    })

    return (
        <div>
            <PageHeader size="large" title="Payments" />

            <Card>
                <CardHeader>
                    <CardTitle>Payment providers</CardTitle>
                    <CardDescription>
                        {data.length} {data.length === 1 ? 'gateway' : 'gateways'},{' '}
                        {data.filter((g) => g.status === 'active').length} active,{' '}
                        {data.filter((g) => g.is_configured).length} configured
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <DataTable columns={columns} data={data} loading={loading} />
                </CardContent>
            </Card>

            {editGateway && (
                <PaymentGatewayDialog
                    open={gatewayDialogOpen}
                    onOpenChange={closeDialog}
                    gateway={editGateway}
                    onSave={handleUpdateGateway}
                    loading={updateLoading}
                />
            )}
        </div>
    )
}
